/**
 * Storage Service
 * Abstraction layer for data persistence
 * Currently uses localStorage but can be easily switched to SQLite via Capacitor
 */

import { AppSettings } from '../types/data';
import { APP_CONFIG, SYNC_CONFIG } from '../config/app.config';

// Storage adapter interface for future implementations (SQLite, etc.)
interface StorageAdapter {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
  clear(): Promise<void>;
}

// LocalStorage adapter (current implementation)
class LocalStorageAdapter implements StorageAdapter {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key);
    } catch (error) {
      console.error('LocalStorage getItem error:', error);
      return null;
    }
  }

  async setItem(key: string, value: string): Promise<void> {
    try {
      // Check if localStorage is available
      if (typeof localStorage === 'undefined') {
        throw new Error('localStorage is not available');
      }
      
      localStorage.setItem(key, value);
      
      // Use requestAnimationFrame to ensure browser has processed the write
      await new Promise<void>(resolve => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            resolve();
          });
        });
      });
    } catch (error: any) {
      // Enhanced error logging
      if (error?.name === 'QuotaExceededError' || error?.code === 22) {
        console.error('LocalStorage quota exceeded', {
          key,
          error: error.message
        });
        throw new Error('Storage quota exceeded. Please clear some data or sync to server.');
      }
      console.error('LocalStorage setItem error:', error);
      throw error;
    }
  }

  async removeItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('LocalStorage removeItem error:', error);
      throw error;
    }
  }

  async clear(): Promise<void> {
    try {
      localStorage.clear();
    } catch (error) {
      console.error('LocalStorage clear error:', error);
      throw error;
    }
  }
}

// Storage service class
class StorageService {
  private adapter: StorageAdapter;
  private authDataListeners = new Set<(authData: any | null) => void>();

  constructor(adapter: StorageAdapter = new LocalStorageAdapter()) {
    this.adapter = adapter;
  }

  // Method to switch adapter at runtime (for storage type switching)
  setAdapter(adapter: StorageAdapter): void {
    this.adapter = adapter;
    console.log('Storage adapter updated');
  }

  // Get current adapter (for manager access)
  getAdapter(): StorageAdapter {
    return this.adapter;
  }

  // Generic get/set methods
  async get<T>(key: string): Promise<T | null> {
    const value = await this.adapter.getItem(key);
    if (!value) return null;
    
    try {
      return JSON.parse(value) as T;
    } catch (error) {
      console.error('Error parsing stored data:', error);
      return null;
    }
  }

  async set<T>(key: string, value: T): Promise<void> {
    try {
      const serialized = JSON.stringify(value);
      await this.adapter.setItem(key, serialized);
    } catch (error: any) {
      // Check for serialization errors
      if (error instanceof TypeError && error.message.includes('circular')) {
        console.error('Circular reference detected in data:', { key, error: error.message });
        throw new Error('Cannot save data: circular reference detected');
      }
      
      // Check for JSON stringify errors
      if (error.message?.includes('stringify')) {
        console.error('JSON serialization failed:', { key, error: error.message });
        throw new Error('Cannot save data: serialization failed');
      }
      
      console.error('Error storing data:', error);
      throw error;
    }
  }

  async remove(key: string): Promise<void> {
    await this.adapter.removeItem(key);
  }

  async clear(): Promise<void> {
    await this.adapter.clear();
  }

  // --- Session-only helpers (cleared when browser tab closes) ---
  async isSessionUnlocked(): Promise<boolean> {
    try {
      return sessionStorage.getItem(APP_CONFIG.STORAGE_KEYS.SESSION_UNLOCKED) === '1';
    } catch (error) {
      console.error('sessionStorage isSessionUnlocked error:', error);
      return false;
    }
  }

  async setSessionUnlocked(unlocked: boolean): Promise<void> {
    try {
      if (unlocked) {
        sessionStorage.setItem(APP_CONFIG.STORAGE_KEYS.SESSION_UNLOCKED, '1');
      } else {
        sessionStorage.removeItem(APP_CONFIG.STORAGE_KEYS.SESSION_UNLOCKED);
      }
    } catch (error) {
      console.error('sessionStorage setSessionUnlocked error:', error);
    }
  }

  // Settings-specific methods
  async getSettings(): Promise<AppSettings> {
    const settings = await this.get<AppSettings>(APP_CONFIG.STORAGE_KEYS.SETTINGS);
    
    if (!settings) {
      // Return default settings
      return this.getDefaultSettings();
    }
    
    return settings;
  }

  async setSettings(settings: Partial<AppSettings>): Promise<void> {
    const currentSettings = await this.getSettings();
    const updatedSettings: AppSettings = {
      ...currentSettings,
      ...settings,
      updatedAt: new Date().toISOString(),
    };
    
    await this.set(APP_CONFIG.STORAGE_KEYS.SETTINGS, updatedSettings);
  }

  async updateSettings(updates: Partial<AppSettings>): Promise<void> {
    await this.setSettings(updates);
  }

  private getDefaultSettings(): AppSettings {
    const now = new Date().toISOString();
    return {
      isFirstLaunch: true,
      hasCompletedOnboarding: false,
      
      biometricEnabled: false,
      
      autoSyncEnabled: true,
      syncInterval: SYNC_CONFIG.DEFAULT_INTERVAL,
      syncOnWifiOnly: false,
      
      offlineMode: false,
      offlineModeWarningShown: false,
      
      theme: 'system',
      language: APP_CONFIG.DEFAULT_LANGUAGE,
      appBackground: 'white',
      banglaFont: 'noto',
      
      dataRetentionDays: 365,
      
      notificationsEnabled: true,
      syncNotificationsEnabled: true,
      
      developerMode: false,
      
      // Survey settings - default field visibility
      surveyNameFieldLabel: 'idOnly', // fullNameOrId or idOnly
      surveyShowMobile: false, // hidden by default for privacy
      surveyShowAge: true,
      surveyMinAge: 0,
      surveyMaxAge: 120,
      surveyShowSex: true,
      surveyShowOccupation: true,
      surveyShowIncome: false, // hidden by default for privacy
      surveyAddressType: 'bangladesh',
      surveyShowAddress: false, // hidden by default for privacy
      
      // Survey UI settings
      surveyShowBothLanguageNames: true, // show both English and Bangla names by default
      surveyOnlyShowRecipesWithImages: true, // only show recipes with images by default
      
      // Nutrient display
      showNutrientOutcome: true, // default: show nutrient outcome
      showNutrientDRI: false, // default: show absolute values only, not DRI bars
      
      // Survey Extra Info settings
      surveyAskExtraInfo: true, // default: ask for additional info
      surveyShowMealLocation: true,
      surveyShowFishSource: true,
      surveyShowPreparedBy: false,
      surveyShowCoIngredients: false,
      surveyShowPeopleSharing: false,      
      surveyShowNotes: false, 
      
      // Meal time restriction
      restrictMealsByTime: false, // default: show all meals always
      
      createdAt: now,
      updatedAt: now,
    };
  }

  // Auth token methods
  async getAuthToken(): Promise<string | null> {
    return await this.adapter.getItem(APP_CONFIG.STORAGE_KEYS.AUTH_TOKEN);
  }

  async setAuthToken(token: string): Promise<void> {
    await this.adapter.setItem(APP_CONFIG.STORAGE_KEYS.AUTH_TOKEN, token);
  }

  async removeAuthToken(): Promise<void> {
    await this.adapter.removeItem(APP_CONFIG.STORAGE_KEYS.AUTH_TOKEN);
  }

  // User data methods (preserved from existing implementation)
  async getUserData(): Promise<any | null> {
    return await this.get(APP_CONFIG.STORAGE_KEYS.USER_DATA);
  }

  async setUserData(data: any): Promise<void> {
    await this.set(APP_CONFIG.STORAGE_KEYS.USER_DATA, data);
  }

  async clearUserData(): Promise<void> {
    await this.remove(APP_CONFIG.STORAGE_KEYS.USER_DATA);
  }

  // Last sync time
  async getLastSyncTime(): Promise<string | null> {
    return await this.adapter.getItem(APP_CONFIG.STORAGE_KEYS.LAST_SYNC);
  }

  async setLastSyncTime(timestamp: string): Promise<void> {
    await this.adapter.setItem(APP_CONFIG.STORAGE_KEYS.LAST_SYNC, timestamp);
  }

  // Storage health check
  async checkStorageHealth(): Promise<{
    available: boolean;
    quotaExceeded: boolean;
    estimatedUsage?: number;
    error?: string;
  }> {
    try {
      // Test write
      const testKey = '__storage_test__';
      const testValue = 'test';
      localStorage.setItem(testKey, testValue);
      localStorage.removeItem(testKey);
      
      // Estimate usage
      let estimatedUsage = 0;
      for (let key in localStorage) {
        if (localStorage.hasOwnProperty(key)) {
          estimatedUsage += localStorage[key].length;
        }
      }
      
      return {
        available: true,
        quotaExceeded: false,
        estimatedUsage
      };
    } catch (error: any) {
      return {
        available: false,
        quotaExceeded: error?.name === 'QuotaExceededError' || error?.code === 22,
        error: error.message
      };
    }
  }

  // First launch check
  async isFirstLaunch(): Promise<boolean> {
    const settings = await this.getSettings();
    return settings.isFirstLaunch;
  }

  async markFirstLaunchComplete(): Promise<void> {
    await this.updateSettings({ isFirstLaunch: false });
  }

  // Auth data methods
  async getAuthData(): Promise<any | null> {
    return await this.get('fishAppAuthData');
  }

  async setAuthData(data: any): Promise<void> {
    await this.set('fishAppAuthData', data);
    this.notifyAuthDataChanged(data);
  }

  async clearAuthData(): Promise<void> {
    await this.remove('fishAppAuthData');
    this.notifyAuthDataChanged(null);
  }

  subscribeAuthData(listener: (authData: any | null) => void): () => void {
    this.authDataListeners.add(listener);
    return () => {
      this.authDataListeners.delete(listener);
    };
  }

  private notifyAuthDataChanged(authData: any | null) {
    this.authDataListeners.forEach((listener) => {
      try {
        listener(authData);
      } catch (error) {
        console.error('Auth data listener error:', error);
      }
    });
  }

  // PIN methods (now use per-user PIN storage)
  private getPinStorageKey(userId?: string): string {
    if (!userId) {
      // Fallback to global key if no user ID provided (shouldn't happen in normal flow)
      return APP_CONFIG.STORAGE_KEYS.PIN_DATA;
    }
    return `${APP_CONFIG.STORAGE_KEYS.PIN_DATA}_${userId}`;
  }

  async isPinEnabled(userId?: string): Promise<boolean> {
    // Migrate legacy PIN storage from authData if present
    await this.migrateLegacyPinData(userId);

    const pinData = await this.get<{ pinHash?: string; pinEnabled?: boolean }>(
      this.getPinStorageKey(userId)
    );
    return !!pinData?.pinEnabled && !!pinData?.pinHash;
  }

  async setPinHash(hash: string, userId?: string): Promise<void> {
    await this.set(this.getPinStorageKey(userId), {
      pinHash: hash,
      pinEnabled: true,
      userId: userId,
      updatedAt: new Date().toISOString(),
    });
  }

  async getPinHash(userId?: string): Promise<string | null> {
    await this.migrateLegacyPinData(userId);
    const pinData = await this.get<{ pinHash?: string }>(this.getPinStorageKey(userId));
    return pinData?.pinHash || null;
  }

  async clearPin(userId?: string): Promise<void> {
    await this.remove(this.getPinStorageKey(userId));
    // Keep legacy fields cleared too (best effort)
    const authData = await this.getAuthData();
    if (authData?.pinHash || authData?.pinEnabled) {
      delete authData.pinHash;
      authData.pinEnabled = false;
      await this.setAuthData(authData);
    }
  }

  private async migrateLegacyPinData(userId?: string): Promise<void> {
    const pinData = await this.get(this.getPinStorageKey(userId));
    if (pinData) return;

    const authData = await this.getAuthData();
    const legacyHash = authData?.pinHash;
    const legacyEnabled = !!authData?.pinEnabled;
    if (!legacyHash || !legacyEnabled) return;

    await this.set(this.getPinStorageKey(userId), {
      pinHash: legacyHash,
      pinEnabled: true,
      userId: userId,
      migratedAt: new Date().toISOString(),
    });
  }

  // Last login user ID (for PIN authentication after logout)
  async setLastLoginUserId(userId: string): Promise<void> {
    await this.set(APP_CONFIG.STORAGE_KEYS.LAST_LOGIN_USER_ID, userId);
  }

  async getLastLoginUserId(): Promise<string | undefined> {
    const userId = await this.get<string>(APP_CONFIG.STORAGE_KEYS.LAST_LOGIN_USER_ID);
    return userId ?? undefined;
  }

  async clearLastLoginUserId(): Promise<void> {
    await this.remove(APP_CONFIG.STORAGE_KEYS.LAST_LOGIN_USER_ID);
  }

  // Clear all app data (logout/reset)
  async clearAllData(): Promise<void> {
    await this.clear();
  }
}

// Function to initialize storage service with the correct adapter
async function initializeStorageService(): Promise<StorageService> {
  const storageTypeKey = 'fishAppStorageType';
  let storageType: 'localStorage' | 'sqlite' = 'localStorage';

  // Try to read storage type preference from localStorage (bootstrap)
  try {
    const savedType = localStorage.getItem(storageTypeKey);
    if (savedType === 'sqlite') {
      storageType = 'sqlite';
    }
  } catch (error) {
    console.warn('Failed to read storage type preference:', error);
  }

  // Create appropriate adapter
  let adapter: StorageAdapter;
  if (storageType === 'sqlite') {
    try {
      // Dynamically import SQLite adapter
      const SQLiteAdapter = (await import('./adapters/sqlite.adapter')).default;
      adapter = new SQLiteAdapter();
      console.log('Initialized with SQLite storage');
    } catch (error) {
      console.error('Failed to initialize SQLite, falling back to localStorage:', error);
      adapter = new LocalStorageAdapter();
    }
  } else {
    adapter = new LocalStorageAdapter();
    console.log('Initialized with localStorage');
  }

  return new StorageService(adapter);
}

// Create singleton instance (will be initialized synchronously with localStorage by default)
export const storageService = new StorageService();

// Initialize with correct adapter asynchronously (should be called on app startup)
export async function initStorage(): Promise<void> {
  try {
    const initializedService = await initializeStorageService();
    // Update the singleton's adapter
    storageService.setAdapter(initializedService.getAdapter());
  } catch (error) {
    console.error('Failed to initialize storage:', error);
    // Keep using localStorage adapter (already set in constructor)
  }
}

/**
 * Whether the "Extra Info" step has any visible content for a fish item.
 * Returns false when the fish has no soft-bone toggle AND either the master
 * "ask extra info" toggle is off or every child field is disabled.
 */
export function shouldShowExtraInfo(
  settings: AppSettings | null,
  hasSoftBone: boolean
): boolean {
  const askExtraInfo = settings?.surveyAskExtraInfo ?? true;
  const anyChildEnabled =
    (settings?.surveyShowMealLocation ?? true) ||
    (settings?.surveyShowFishSource ?? true) ||
    (settings?.surveyShowPreparedBy ?? true) ||
    (settings?.surveyShowCoIngredients ?? true) ||
    (settings?.surveyShowPeopleSharing ?? true) ||
    (settings?.surveyShowNotes ?? false);

  return hasSoftBone || (askExtraInfo && anyChildEnabled);
}

// Export class for testing or custom instantiation
export default StorageService;
